/**
 * User Domain Types
 *
 * Mirrors backend domain types for type safety
 */

import type { UserRole } from '@/config/constants'

export interface User {
  id: string
  email: string
  role: UserRole
  firstName: string | null
  lastName: string | null
  secondLastName: string | null
  phone: string | null
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PublicUser {
  id: string
  email: string
  role: UserRole
  firstName: string | null
  lastName: string | null
  secondLastName: string | null
  phone: string | null
  isActive: boolean
  mustChangePassword?: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
  /**
   * Permisos efectivos, tal como los resuelve el servidor.
   *
   * La interfaz los consulta para no ofrecer lo que después se va a negar.
   * NO es un control de seguridad: el servidor decide en cada ruta. Es
   * opcional porque una sesión abierta antes de este cambio no los trae.
   */
  permissions?: string[]
  /**
   * Módulos habilitados para su institución (ver backend: domain/institution/modulos).
   * Lo que no aparece se considera habilitado, igual que en el servidor.
   */
  modulos?: Record<string, boolean>
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  user: PublicUser
  accessToken: string
  refreshToken: string
}

/**
 * Radiologist with workload stats (for assignment dropdown)
 */
export interface RadiologistWithWorkload {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  fullName: string // firstName + lastName or email as fallback
  role: UserRole
  isActive: boolean
  pendingCount: number
  inProgressCount: number
}

/**
 * Response from GET /users/radiologists
 */
export interface RadiologistsResponse {
  radiologists: RadiologistWithWorkload[]
}

/**
 * User with institution (for admin views)
 */
export interface UserWithInstitution extends User {
  institutionId: string | null
}

/**
 * Create user data
 */
export interface CreateUserData {
  email: string
  password: string
  role: UserRole
  firstName?: string
  lastName?: string
  secondLastName?: string
  phone?: string
  institutionId?: string
}

/**
 * Update user data
 */
export interface UpdateUserData {
  email?: string
  role?: UserRole
  firstName?: string | null
  lastName?: string | null
  secondLastName?: string | null
  phone?: string | null
  isActive?: boolean
}

/**
 * Reset password data
 */
export interface ResetPasswordData {
  newPassword: string
}

/**
 * User search filters
 */
export interface UserSearchFilters {
  search?: string
  role?: UserRole
  isActive?: boolean
  institutionId?: string
  limit?: number
  offset?: number
}

/**
 * User list response
 */
export interface UserListResponse {
  users: UserWithInstitution[]
  total: number
  limit: number
  offset: number
}

/**
 * El nombre con el que se le habla al usuario en pantalla.
 *
 * POR QUE EXISTE
 * ==============
 * La bienvenida y la barra lateral partian el correo por la arroba, asi que al
 * radiologo le decian "Bienvenido, radiologist" mientras el panel del
 * administrador, que si usa el nombre, lo llamaba "Rocio Restrepo". El mismo
 * usuario con dos nombres en el mismo producto.
 *
 * El correo queda como ultimo recurso: un usuario recien creado puede no tener
 * nombre todavia, y ahi es mejor "jperez" que un hueco.
 */
export const nombreParaMostrar = (
  usuario: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined
): string => {
  const nombre = [usuario?.firstName, usuario?.lastName].filter(Boolean).join(' ').trim()
  if (nombre) return nombre

  return usuario?.email?.split('@')[0] ?? ''
}
